import { memo, useCallback, useContext, useEffect, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import MatterDropdownWithForm from '../common/MatterDropdownWithForm'
import { InfoOutlined } from '@mui/icons-material'
import { Autocomplete, Box, FormControl, ListItem, TextField } from '@mui/material'
import { AppContext } from '../../context'
import { EntryContext } from '../../context/EntryContext'
import UserDropdownWithForm from '../common/UserDropDownWithForm'
import { EntryRateType, EntryType, Maybe, useFindOneUserQuery, User, UserRole, UserType } from '../../../generated'
import { badgeBgColor, capitalizeCase, getPrimaryRole, handleDuration } from '../../utils'
import { assigneeDropdownStyle, flexCenter } from '../../theme/styleConstants'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST, TIMER_ROUNDING_INCREMENT_MINUTES } from '../../constants'

export const SelectCase = memo(
  ({
    rateValue,
    initialCase,
    lockCase = false
  }: {
    rateValue: EntryRateType
    initialCase?: Parameters<typeof MatterDropdownWithForm>[0]['initialCase']
    /** The entry belongs to a fixed matter (e.g. the matter detail page) — the matter cannot be changed. */
    lockCase?: boolean
  }) => {
    const { control } = useFormContext()
    // `useWatch`, not `watch`: this component is memoised, and `watch` only
    // re-renders the component that owns `useForm` (the dialog). With its props
    // unchanged, memo then blocked this one from re-rendering, so it kept the
    // userId from its first render — usually empty — and the matter query went
    // out unscoped. `useWatch` subscribes here, so the value actually updates.
    const userId = useWatch({ name: 'userId', control })

    return (
      <FormControl variant="outlined" sx={{ marginBottom: 1 }} fullWidth>
        <Controller
          name="caseId"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <MatterDropdownWithForm
              name="caseId"
              label="Select Matter"
              // Always the *selected* user, admins included — the list is the
              // matters that user is on, not the matters of whoever is logged
              // in. With no user there is nothing to scope to, and an empty
              // userId is NOT the same as omitting it: the query reads it as
              // "no filter" and answers with every matter in the firm. Keeping
              // the field closed until a user is picked stops that request
              // being made at all (nothing is fetched until the list opens).
              userId={userId}
              required
              disabled={lockCase || !userId}
              helperText={!lockCase && !userId ? 'Select a user first' : undefined}
              error={!!error}
              setRate={rateValue === EntryRateType.Matter}
              seedCaseNumber
              initialCase={initialCase}
            />
          )}
        />
      </FormControl>
    )
  }
)

export const SelectUser = ({
  label,
  rateValue,
  onUserResolved
}: {
  label: string
  rateValue: EntryRateType
  /** Reports the selected user (with their profile hourlyRate) to the parent so
   *  it can set the Profile rate without a separate findOneUser query. */
  onUserResolved?: (user: Maybe<User> | null) => void
}) => {
  const { isCostEntry, entryType, isTimeEntry } = useContext(AppContext)
  const { currentEntry } = useContext(EntryContext)
  const { setValue } = useFormContext()

  // The matter list is scoped to the selected user, so the moment that user is
  // cleared or swapped the chosen matter no longer belongs to the form — it
  // would otherwise sit there looking selected behind a disabled field, and get
  // submitted with someone else's rate. Only fires on a real interaction, never
  // on a reset (which sets userId and caseId together).
  const handleUserChange = useCallback(() => {
    setValue('caseId', '')
    setValue('caseNumber', '')
    if (rateValue === EntryRateType.Matter) setValue('rate', 0)
  }, [rateValue, setValue])

  // The matter's legal team, each once even when they hold several seats.
  const legalTeam = useMemo(() => {
    if (!currentEntry?.case?.caseToUsers) return null
    const users = new Map<string, User>()
    currentEntry.case.caseToUsers.forEach(ctu => {
      if (!ctu?.user?.id || ctu.userType === UserType.Creator || ctu.userType === UserType.OriginatorAttorney) return
      users.set(ctu.user.id, ctu.user as User)
    })
    return users
  }, [currentEntry?.case?.caseToUsers])

  // Whoever opened the file is offered too. On a matter converted from a consult,
  // `openedBy` opened the consult and `convertedToNormalMatterBy` opened the file
  // (as the Matter Details card shows them). The matter query carries no roles or
  // rate for them, so they are looked up here — only when they are not on the team.
  const { convertedToNormalMatter, convertedToNormalMatterBy, openedBy } = currentEntry?.case || {}
  const fileOpenerId = (convertedToNormalMatter ? convertedToNormalMatterBy : openedBy)?.id
  const fileOpenerOnTeam = !!fileOpenerId && !!legalTeam?.has(fileOpenerId)
  const { data: fileOpenerData } = useFindOneUserQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    skip: !legalTeam || !fileOpenerId || fileOpenerOnTeam,
    variables: { findOneUserId: fileOpenerId || '' }
  })
  const fileOpener = fileOpenerData?.findOneUser?.user

  const caseTeamUsers = useMemo(() => {
    if (!legalTeam) return null
    const users = Array.from(legalTeam.values())
    if (fileOpener?.id && !legalTeam.has(fileOpener.id)) users.push(fileOpener as User)
    return users
  }, [legalTeam, fileOpener])

  if (caseTeamUsers) {
    return (
      <CaseTeamUserDropdown
        label={label}
        name="userId"
        users={caseTeamUsers}
        setRate={rateValue === EntryRateType.Profile}
        onUserResolved={onUserResolved}
      />
    )
  }

  return (
    <UserDropdownWithForm
      label={label}
      name="userId"
      required
      setRate={rateValue === EntryRateType.Profile}
      onUserResolved={onUserResolved}
      onUserChange={handleUserChange}
      sx={{
        minWidth: 150,
        marginBottom: 2
      }}
      roles={
        isCostEntry || entryType === EntryType.Cost
          ? [UserRole.Investigator]
          : isTimeEntry || entryType === EntryType.Time
          ? [UserRole.Attorney, UserRole.Paralegal, UserRole.Admin]
          : undefined
      }
    />
  )
}

const CaseTeamUserDropdown = ({
  label,
  name,
  users,
  setRate,
  onUserResolved
}: {
  label: string
  name: string
  users: Maybe<User>[]
  setRate: boolean
  onUserResolved?: (user: Maybe<User> | null) => void
}) => {
  const { control, setValue, watch } = useFormContext()
  const selectedUserId = watch(name)

  // Report the pre-selected user (edit mode) once the team list is available,
  // so the parent knows its profile rate without querying for it.
  useEffect(() => {
    onUserResolved?.(users.find(user => user?.id === selectedUserId) || null)
  }, [users, selectedUserId, onUserResolved])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const selectedUser = users.find(u => u?.id === field.value) || null

        return (
          <Autocomplete
            options={users}
            value={selectedUser}
            onChange={(_, newValue) => {
              field.onChange(newValue?.id || null)
              onUserResolved?.(newValue)
              if (setRate && newValue) {
                setValue('rate', newValue.hourlyRate || 0)
              }
            }}
            getOptionLabel={option =>
              `${capitalizeCase(option?.firstName || '')} ${capitalizeCase(option?.lastName || '')}`.trim()
            }
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={(props, option) => {
              const role = getPrimaryRole(option?.roles) || ''
              const roleColor = role === UserRole.Admin ? role : role.slice(0, 1)
              const fullName = `${capitalizeCase(option?.firstName || '')} ${capitalizeCase(
                option?.lastName || ''
              )}`.trim()

              return (
                <ListItem {...props} key={option?.id}>
                  <Box position="relative">
                    {fullName}
                    <Box
                      component="span"
                      sx={{ ...assigneeDropdownStyle, ...flexCenter, background: badgeBgColor(roleColor) }}
                    >
                      {role.slice(0, 1)}
                    </Box>
                  </Box>
                </ListItem>
              )
            }}
            renderInput={params => <TextField {...params} label={label} error={!!error} helperText={error?.message} />}
            sx={{ minWidth: 150, marginBottom: 2 }}
          />
        )
      }}
    />
  )
}

/** Sits under the field while the clock owns the value, so it is clear the
 *  duration is not editable here rather than looking broken — and that the
 *  number above is not the number that gets saved, since the server rounds it
 *  up to the next billing increment on stop. */
const ClockModeHint = () => (
  <Box component="span" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
    <InfoOutlined sx={{ fontSize: '0.95rem', mt: '1px', flexShrink: 0 }} />
    <Box component="span">
      Time is billed in {TIMER_ROUNDING_INCREMENT_MINUTES}-minute
      increments and rounds up when the clock is stopped.
    </Box>
  </Box>
)

export const AddDuration = memo(({ readOnly = false }: { readOnly?: boolean }) => {
  const { setValue, control } = useFormContext()

  return (
    <Controller
      name="duration"
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl variant="outlined" sx={{ marginTop: 1 }} fullWidth>
          <TextField
            {...field}
            value={field.value ?? ''}
            label="Duration (hh:mm:ss)"
            placeholder="00:00:00"
            // Without these the schema had nowhere to report to, so an invalid
            // duration was invisible — the blur handler just emptied the field.
            error={!!error}
            helperText={error?.message ?? (readOnly ? <ClockModeHint /> : undefined)}
            // Read-only rather than disabled: the value stays legible and
            // selectable, and it is the only field held back while a timer is
            // stopped — everything around it is editable. This used to lean on
            // the whole form being a disabled <fieldset>, which is gone now.
            InputProps={{ readOnly }}
            onBlur={() => (readOnly ? undefined : handleDuration(field.value, setValue))}
            onKeyDown={e => {
              if (readOnly) return
              if (e.key === 'Enter') {
                e.preventDefault()
                handleDuration(field.value, setValue)
              }
            }}
          />
        </FormControl>
      )}
    />
  )
})
